'use client';

import { useEffect } from 'react';
import { reportError, serializeForTelemetry } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';

interface ErrorHandlerProviderProps {
  children: React.ReactNode;
}

/**
 * Global error capture. Mounts two `window` listeners:
 *
 * 1. `unhandledrejection` — async errors that nothing `await`ed / `.catch`ed.
 * 2. `error`              — sync errors fired from non-React code paths
 *                            (event handlers, image onerror, postMessage
 *                            handlers, etc.). React render-time errors do
 *                            NOT reach here — they're caught by the
 *                            nearest `error.tsx`.
 *
 * Both paths feed the original raw value into `serializeForTelemetry`
 * before forwarding. That's load-bearing — by the time a structured throw
 * (e.g. `PersistenceShapeViolationError` carrying `category`/`path`/`kind`)
 * has been wrapped by Next's dev overlay, the only field left is
 * `"[object Object]"`. We capture the raw value first.
 */
export function ErrorHandlerProvider({ children }: ErrorHandlerProviderProps): React.ReactNode {
  useEffect(() => {
    function handleUnhandledRejection(event: PromiseRejectionEvent): void {
      event.preventDefault();
      // logger.error routes to console in dev (so the structured payload
      // is visible alongside Next's overlay) and to Better Stack on
      // Vercel. We pass the serialized payload as context so every
      // diagnostic field becomes a searchable column.
      logger.error('unhandled promise rejection', {
        ...serializeForTelemetry(event.reason),
        source: 'unhandledrejection',
      });
      reportError(event.reason, {
        source: 'unhandledrejection',
        url: window.location.href,
      });
    }

    function handleError(event: ErrorEvent): void {
      // `event.error` is `null` for cross-origin script errors — fall back
      // to whatever the browser exposes so we still get a row in telemetry.
      // ALLOW-UNKNOWN: window.onerror surfaces arbitrary thrown values; serializer accepts unknown.
      const raw: unknown =
        event.error ??
        ({
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        } as const);
      logger.error('uncaught window error', {
        ...serializeForTelemetry(raw),
        source: 'window.error',
        filename: String(event.filename ?? 'unknown'),
        line: String(event.lineno ?? 0),
        col: String(event.colno ?? 0),
      });
      reportError(raw, {
        source: 'window.error',
        url: window.location.href,
        filename: String(event.filename ?? 'unknown'),
        line: String(event.lineno ?? 0),
        col: String(event.colno ?? 0),
      });
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return (): void => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return <>{children}</>;
}
